import { ReactComponent as Delete } from "assets/icons/delete.svg";
import { ReactComponent as DetailsIcon } from "assets/icons/details.svg";
import { ReactComponent as Edit } from "assets/icons/edit.svg";
import { format } from "date-fns";
import { useResponsiveWidth } from "hooks/useResponsiveWidth";
import {
    Button,
    Checkbox,
    ConfirmationModal,
    CustomTable,
    NavDropdown,
    OptionType,
    Pagination,
    RoleType,
    TableType,
} from "modules/common";
import { DocumentType, documentTypeFilterFields } from "modules/educationDocuments/constants";
import { useContext, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { SortingRule } from "react-table";
import {
    EducationDocument,
    EducationDocumentPaginatedResponse,
} from "services/educationDocuments/response";
import { RootState } from "store";
import { AccessibilityContext } from "views/AccessibilityContext";
import { downloadFile, useBulkDeleteEducationDocuments } from "../hooks";
import { EducationDocumentDetailsModal } from "./EducationDocumentsDetailsModal";

export interface IEducationDocumentsTable {
    educationDocuments?: EducationDocumentPaginatedResponse;
    isLoading?: boolean;
    setSortBy?: (sortBy: SortingRule<object>) => void;
    page?: number;
    pageSize?: number;
    onPageSizeChange?: (value: number) => void;
    onPageChange?: (page: number) => void;
    className?: string;
    selectedRows: EducationDocument[];
    setSelectedRows: (rows: EducationDocument[]) => void;
    setIsDocumentModalOpen: (isOpen: boolean) => void;
    setIsOnlyOneChecked: (isChecked: boolean) => void;
}

const DefaultPageSize = {
    value: "5",
    label: "5",
};

export const EducationDocumentsTable = ({
    educationDocuments,
    isLoading,
    setSortBy,
    page,
    pageSize,
    onPageSizeChange,
    onPageChange,
    className,
    selectedRows,
    setSelectedRows,
    setIsDocumentModalOpen,
    setIsOnlyOneChecked,
}: IEducationDocumentsTable) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<EducationDocument>();
    const { profile } = useSelector((state: RootState) => state.profile);
    const bulkDelete = useBulkDeleteEducationDocuments();
    const [showBulkDelete, setShowBulkDelete] = useState(false);
    const [isOneChecked, setIsOneChecked] = useState(false);

    const isMobileOrTablet = useResponsiveWidth();

    const isEquipmentAdmin = profile?.roleType?.value === RoleType.EquipmentAdmin;
    const isCommunicatorAdviser = profile?.roleType?.value === RoleType.CommunicatorAdviser;
    const isMinistryAdviser = profile?.roleType?.value === RoleType.MinistryAdviser;

    const getDocumentTypeName = (documentType: DocumentType): string => {
        return (
            documentTypeFilterFields.find(field => field.value === documentType)?.label ??
            "Nepoznato"
        );
    };

    const confirmationModalContent = useMemo(() => {
        return `Jeste li sigurni da želite trajno izbrisati odabrane dokumente? \n\n ${selectedRows
            .map(row => row.name)
            .join("\n\n")}`;
    }, [selectedRows]);

    const { lowVision } = useContext(AccessibilityContext);

    const baseTableColumns: any[] = [
        {
            Header: "NAZIV",
            accessor: "name",
            disableSortBy: true,
            width: 10,
            Cell: ({ row }: any) => {
                const { original } = row;
                return (
                    <span
                        className="modal-name"
                        onClick={() => {
                            setIsOneChecked(false);
                            setModalOpen(true);
                            setSelectedRow(original);
                        }}
                    >
                        {original.name}
                    </span>
                );
            },
        },
        {
            Header: "TIP DOKUMENTA",
            accessor: "documentType",
            disableSortBy: true,
            width: 4,
            Cell: ({ row }: any) => {
                const { original } = row;
                return (
                    <span className="modal-name">{getDocumentTypeName(original.documentType)}</span>
                );
            },
        },
        {
            Header: "DATUM",
            accessor: "lastUpdated",
            disableSortBy: true,
            width: 4,
            Cell: ({ row }: any) => {
                const { original } = row;
                return (
                    <span className="modal-name">
                        {format(new Date(original.lastUpdated), "dd.MM.yyyy.")}
                    </span>
                );
            },
        },
        {
            Header: "VIŠE",
            accessor: "action-info",
            disableSortBy: true,
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return (
                    <Button
                        size="xxsmall"
                        className="btn--info"
                        onClick={() => {
                            setModalOpen(true);
                            setIsOneChecked(false);
                            setSelectedRow(original);
                        }}
                        disabled={false}
                    >
                        <DetailsIcon></DetailsIcon>
                    </Button>
                );
            },
        },
        {
            Header: "PREUZIMANJE",
            id: "actions",
            disableSortBy: true,
            width: 3,
            className: "actions-cell",
            Cell: ({ row }: any) => {
                return (
                    <Button
                        size="small"
                        variant="primary-ghost"
                        lowVision={lowVision}
                        onClick={() => {
                            const { original } = row;
                            downloadFile(original.fileUrl, original.name);
                        }}
                        disabled={false}
                    >
                        Preuzmi
                    </Button>
                );
            },
        },
    ];

    const checkboxColumn = {
        Header: () =>
            selectedRows.length > 0 && (
                <div className="table-header-dropdown-container">
                    <NavDropdown name="records" label="">
                        <div
                            className="table-header-dropdown-item"
                            onClick={() => {
                                setIsOnlyOneChecked(selectedRows.length === 1);
                                setIsOneChecked(selectedRows.length === 1);
                                setIsDocumentModalOpen(true);
                            }}
                        >
                            UREDI
                            <Edit />
                        </div>
                        <div
                            className="table-header-dropdown-item"
                            onClick={() => {
                                setShowBulkDelete(true);
                            }}
                        >
                            OBRIŠI
                            <Delete />
                        </div>
                    </NavDropdown>
                </div>
            ),
        accessor: "checkbox",
        disableSortBy: true,
        width: 2,
        Cell: ({ row }: any) => {
            const { original } = row;
            return (
                <Checkbox
                    isChecked={selectedRows.some(row => row.guid === original.guid)}
                    onChange={event => {
                        if (event.target.checked) {
                            setSelectedRows([...selectedRows, original]);
                        } else {
                            setSelectedRows(selectedRows.filter(row => row.guid !== original.guid));
                        }
                    }}
                    type="checkbox"
                    htmlFor={original.guid}
                    text=" "
                    isDisabled={profile?.roleType?.value !== RoleType.SuperAdmin}
                />
            );
        },
    };

    const tableColumns = useMemo(() => {
        return isEquipmentAdmin || isCommunicatorAdviser || isMinistryAdviser
            ? baseTableColumns
            : [checkboxColumn, ...baseTableColumns];
    }, [
        isEquipmentAdmin,
        isCommunicatorAdviser,
        isMinistryAdviser,
        checkboxColumn,
        baseTableColumns,
    ]);

    const activePageSize = useMemo(() => {
        return pageSize
            ? { value: pageSize.toString(), label: pageSize.toString() }
            : DefaultPageSize;
    }, [pageSize]);

    const classNameString = useMemo(() => {
        return `education-documents-table ${className ?? ""}`;
    }, [className]);

    const handlePageSizeChange = (option: OptionType | null) => {
        onPageSizeChange && onPageSizeChange(parseInt(option?.value ?? DefaultPageSize.value));
    };

    const handlePageClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        const target = event.currentTarget ?? event.target;
        onPageChange && onPageChange(Number(parseInt(target.id)));
    };

    const handleNexPageClick = () => {
        onPageChange && onPageChange(page ? page + 1 : 1);
    };

    const handlePrevPageClick = () => {
        onPageChange && onPageChange(page ? page - 1 : 1);
    };

    const handlePageSelect = (page: number) => {
        onPageChange && onPageChange(page);
    };

    const handleFirstPageClick = () => {
        onPageChange && onPageChange(1);
    };

    const handleLastPageClick = () => {
        if (educationDocuments?.items && educationDocuments?.items.length > 0)
            onPageChange &&
                onPageChange(Math.ceil(educationDocuments.total / parseInt(activePageSize.value)));
    };

    const handleBulkDeleteEducationDocuments = () => {
        const educationDocumentGuids = selectedRows.map(row => row.guid);
        bulkDelete.mutate(educationDocumentGuids);
        setSelectedRows([]);
        setIsOnlyOneChecked(false);
        setShowBulkDelete(false);
    };

    return (
        <div className={classNameString}>
            <div className="education-documents-custom-table">
                <CustomTable
                    flex
                    data={educationDocuments?.items ?? []}
                    columns={tableColumns}
                    loading={isLoading}
                    setSortBy={setSortBy}
                    tableType={TableType.AdministrationTable}
                />
            </div>
            {onPageChange && educationDocuments && educationDocuments.items.length > 0 && (
                <Pagination
                    className="mt-24 education-documents-table-pagination"
                    menuPlacement={isMobileOrTablet ? "top" : "bottom"}
                    currentPage={page ?? 1}
                    numberOfPages={Math.ceil(
                        educationDocuments.total / parseInt(activePageSize.value)
                    )}
                    pageNeighbours={1}
                    pageSize={activePageSize}
                    onPageSizeChange={handlePageSizeChange}
                    handlePageClick={handlePageClick}
                    handlePageSelect={handlePageSelect}
                    next={handleNexPageClick}
                    prev={handlePrevPageClick}
                    first={handleFirstPageClick}
                    last={handleLastPageClick}
                />
            )}

            {modalOpen && (
                <EducationDocumentDetailsModal
                    isOpen={modalOpen}
                    handleClose={() => setModalOpen(false)}
                    educationDocuments={selectedRow ? [selectedRow] : []}
                    isChecked={isOneChecked}
                    page={page}
                    onPageChange={onPageChange}
                />
            )}
            <ConfirmationModal
                isActive={showBulkDelete}
                onConfirm={handleBulkDeleteEducationDocuments}
                onCancel={() => setShowBulkDelete(false)}
                confirmText="OBRIŠI"
                cancelText="ODUSTANI"
                confirmButtonVariant="warning-ghost"
                cancelButtonVariant="primary-ghost"
                title="Brisanje datoteka"
                content={confirmationModalContent}
            />
        </div>
    );
};
